import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { IDataSource } from './datasource.interface';
import { AbstractDataService, SFSOptions } from './abstractdata.service';
import { BaseModel } from '../models/basemodel';
import { Pageable } from '../models/pageable';

export class NimmstaDataSource<T extends BaseModel> implements DataSource<T>, IDataSource<T> {
  protected dataSubject = new BehaviorSubject<T[]>([]);

  protected loadingSubject = new BehaviorSubject<boolean>(true);

  public loading = this.loadingSubject.asObservable();

  public count = 0;

  public data?: Pageable<T> = undefined;

  public defaultFilter: string[] = [];

  constructor(protected dataService: AbstractDataService<T>) {}

  connect(_: CollectionViewer): Observable<readonly T[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(_: CollectionViewer): void {
    this.dataSubject.complete();
    this.loadingSubject.complete();
  }

  loadData(options: Partial<SFSOptions> = {}): Observable<Pageable<T>> {
    const shouldShowLoading = options.removeDataOnLoad || !this.data;
    if (options.removeDataOnLoad) {
      this.dataSubject.next([]);
      this.count = 0;
      this.data = undefined;
    }
    if (shouldShowLoading) {
      this.loadingSubject.next(true);
    }
    return this.dataService.list({
      ...options,
      filter: options.filter?.concat(this.defaultFilter) || this.defaultFilter,
    }).pipe(
      finalize(() => {
        if (shouldShowLoading) {
          this.loadingSubject.next(false);
        }
      }),
      tap((data) => {
        const transformedData = this.loadedDataTransform(data);
        if (transformedData !== undefined) {
          this.dataSubject.next(transformedData.content);
          this.count = transformedData.totalElements;
          this.data = transformedData;
        } else {
          this.dataSubject.next(data.content);
          this.count = data.totalElements;
          this.data = data;
        }
        if (shouldShowLoading) {
          this.loadingSubject.next(false);
        }
      }),
    );
  }

  // Overwrite this method to transform the loaded data in subclasses
  protected loadedDataTransform(data: Pageable<T>): Pageable<T> {
    return data;
  }
}
